import { validate as validateUuid } from 'uuid';

import type {
  PartBotResults,
  PartBotResultJson,
  PartBotResultEntries,
  PartUuid,
  ResultValueMap,
} from './types';

/**
 * Count the total findings from the partbot results
 */
export const getTotalPartBotFindings = (
  results?: PartBotResults | null,
): { totalCount: number; categoryCount: number; pillCount: number } => {
  const rawResults = (results ?? {}).raw ?? [];
  const formattedResults = (results || {}).formatted ?? {};

  let pillCount = 0;
  for (const valueMap of Object.values(formattedResults)) {
    // each unique value in a category will create a pill
    pillCount += Object.keys(valueMap).length;
  }
  return {
    totalCount: rawResults.length,
    categoryCount: Object.keys(formattedResults).length,
    pillCount,
  };
};

/**
 * Format the partbot API response into a formatted object
 * FIXME: We don't _really_ need this, but a lot of code still uses the "formatted" key. Get rid of it if you have the opportunity
 */
export const formatPartBotResults = (results: PartBotResultJson[]) => {
  const _result: PartBotResultEntries = {};
  for (const rawPartBotResult of results) {
    const { category, value } = rawPartBotResult;

    if (category in _result) {
      const valueMap = _result[category];
      if (value in valueMap) {
        valueMap[value].push(rawPartBotResult);
      } else {
        valueMap[value] = [rawPartBotResult];
      }
    } else {
      _result[category] = { [value]: [rawPartBotResult] };
    }
  }

  return {
    raw: results,
    formatted: _result,
    // Order is preserved by the results, so we don't need to sort them
    // Mapped by group, then category, then value
    withGroupsDeduplicated: results.reduce<
      Record<string, Record<string, ResultValueMap>>
    >((acc, result) => {
      const { category, group } = result;
      if (group in acc) {
        if (category in acc[group]) {
          acc[group][category][result.value] = [
            ...(acc[group][category][result.value] || []),
            result,
          ];
        } else {
          acc[group][category] = { [result.value]: [result] };
        }
      } else {
        acc[group] = { [category]: { [result.value]: [result] } };
      }
      return acc;
    }, {}),
    // New structure: group -> category -> array (no value de-duplication)
    withGroupsDuplicated: results.reduce<
      Record<string, Record<string, PartBotResultJson[]>>
    >((acc, result) => {
      const { group, category } = result;
      if (group in acc) {
        if (category in acc[group]) {
          acc[group][category].push(result);
        } else {
          acc[group][category] = [result];
        }
      } else {
        acc[group] = { [category]: [result] };
      }
      return acc;
    }, {}),
  };
};

export const isValidPartUuid = (partUuid: PartUuid | null | undefined) => {
  if (partUuid && typeof partUuid === 'string') {
    return validateUuid(partUuid);
  }
  return false;
};
